import { useState, useRef, useEffect } from "react";
import { Check, Loader2 } from "lucide-react";

/**
 * ActionButton — bouton d'action avec retour visuel CORAIL à la validation.
 *
 * Exécute `onClick` (sync ou async) :
 *  - pendant l'exécution : spinner + bouton désactivé,
 *  - au succès : flash corail (#C26B53) + ✓ pendant ~1.3s, puis retour à l'état normal.
 *
 * props :
 *  - onClick(e) : handler (peut être async). Tolère l'absence d'event.
 *  - className  : classes du bouton (le flash corail s'applique par-dessus via !important).
 *  - type       : "button" (défaut) | "submit"
 *  - successLabel : libellé affiché pendant le flash (défaut "Validé").
 *  - testid     : data-testid.
 */
export default function ActionButton({
  onClick,
  className = "",
  children,
  type = "button",
  disabled = false,
  testid,
  successLabel = "Validé",
  ...rest
}) {
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const mounted = useRef(true);
  const timerRef = useRef(null);

  useEffect(() => () => { mounted.current = false; if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handle = async (e) => {
    if (status === "loading") return;
    try {
      setStatus("loading");
      await onClick?.(e);
      if (!mounted.current) return;
      setStatus("done");
      timerRef.current = setTimeout(() => { if (mounted.current) setStatus("idle"); }, 1300);
    } catch (err) {
      if (mounted.current) setStatus("idle");
      console.error("ActionButton onClick error:", err);
    }
  };

  const doneCls = status === "done" ? "!bg-[#C26B53] !text-white !border-[#C26B53]" : "";

  return (
    <button
      type={type}
      data-testid={testid}
      disabled={disabled || status === "loading"}
      onClick={handle}
      className={`${className} ${doneCls} transition-colors disabled:opacity-70`}
      {...rest}
    >
      {status === "loading" && <Loader2 size={14} className="inline animate-spin -mt-0.5 mr-1" />}
      {status === "done" && <Check size={14} className="inline -mt-0.5 mr-1" />}
      {status === "done" ? successLabel : children}
    </button>
  );
}
